import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import type { FrameData } from "@/lib/frame-data";
import { useProjectStore } from "@/stores/project-store";
import { useTasksStore, isWithinCreatedRange } from "@/stores/tasks-store";
import {
  boardDefaultProjects,
  type BoardProjects,
} from "@/stores/task-view-session";
import type { Task } from "@/api/tasks";
import { taskAssigneeName } from "./task-state";

type DisplayStore = ReturnType<typeof useTasksStore.getState>;
export type TaskDisplayState = Pick<
  DisplayStore,
  | "showCompleted"
  | "sortBy"
  | "sortDirection"
  | "filterStatus"
  | "filterPriority"
  | "filterAgent"
  | "filterSource"
  | "filterCreatedRange"
>;
export function useTaskDisplayState(): TaskDisplayState {
  return useTasksStore(
    useShallow((state) => ({
      showCompleted: state.showCompleted,
      sortBy: state.sortBy,
      sortDirection: state.sortDirection,
      filterStatus: state.filterStatus,
      filterPriority: state.filterPriority,
      filterAgent: state.filterAgent,
      filterSource: state.filterSource,
      filterCreatedRange: state.filterCreatedRange,
    })),
  );
}
/**
 * The tasks a board shows: those of the user's active projects (a task of an
 * archived project drops out without a refetch), all or one project's.
 */
export function boardTasks(
  tasks: readonly Task[],
  projects: BoardProjects,
  activeProjectIds: ReadonlySet<string>,
): Task[] {
  return tasks.filter(
    (task) =>
      activeProjectIds.has(task.project) &&
      (projects === "all" || task.project === projects),
  );
}

/** `boardTasks` over the store's tasks and the project store's active projects. */
export function useBoardTasks(projects: BoardProjects): Task[] {
  const tasks = useTasksStore((state) => state.tasks);
  const activeProjects = useProjectStore((state) => state.projects);
  return useMemo(
    () =>
      boardTasks(
        tasks,
        projects,
        new Set(activeProjects.map((project) => project.id)),
      ),
    [tasks, projects, activeProjects],
  );
}

/**
 * A board frame's project: the one the frame names when it is an active
 * project, else the one this window shows (a pin window's frame may still
 * carry a canvas space id).
 */
export function useBoardProjectId(
  frame: Pick<FrameData, "spaceId">,
): string | undefined {
  return useProjectStore((state) =>
    state.projects.some((project) => project.id === frame.spaceId)
      ? frame.spaceId
      : state.currentProject?.id,
  );
}

/**
 * A board frame's tasks as it opens: its own project's, Home's on All; null
 * while the window shows no project yet.
 */
export function useFrameBoardTasks(
  frame: Pick<FrameData, "spaceId">,
): Task[] | null {
  const projectId = useBoardProjectId(frame);
  const projects = useProjectStore((state) => state.projects);
  const tasks = useBoardTasks(
    projectId ? boardDefaultProjects(projectId, projects) : "all",
  );
  return projectId ? tasks : null;
}

/** Board keeps its six statuses; hidden controls must never change that view. */
export function filterTasksForView(
  tasks: readonly Task[],
  state: TaskDisplayState,
  view: "board" | "list" | "calendar",
): Task[] {
  return tasks.filter(
    (task) =>
      (view === "board" || state.showCompleted || task.status !== "done") &&
      (view === "board" ||
        state.filterStatus === "all" ||
        task.status === state.filterStatus) &&
      (state.filterPriority === "all" ||
        task.priority === state.filterPriority) &&
      (!state.filterAgent || task.assigned_agent === state.filterAgent) &&
      (state.filterSource === "all" || task.source === state.filterSource) &&
      (state.filterCreatedRange === "all" ||
        isWithinCreatedRange(task.created_at, state.filterCreatedRange)),
  );
}

/** The tasks whose title, ticket or assignee name contains the query. */
export function tasksMatching(
  tasks: readonly Task[],
  query: string,
): readonly Task[] {
  const needle = query.toLowerCase();
  if (!needle) return tasks;
  return tasks.filter((task) =>
    [task.title, task.ticket_display, taskAssigneeName(task)].some((text) =>
      text?.toLowerCase().includes(needle),
    ),
  );
}
